import React, { useMemo } from 'react';
import { Box, SxProps, Theme } from '@mui/material';
import { bars as generateBars, WaveformBar } from './waveformBars';

interface WaveformProps {
  /** Number of bars to generate. Ignored when `bars` is supplied. */
  count?: number;
  /** Per-project offset — the same seed always yields the same waveform. */
  seed?: number;
  /** Max bar height in px. */
  height: number;
  /** Playback progress 0..1. Omit for the idle (never-played) styling. */
  played?: number;
  /** Pre-generated bars, e.g. from a fixture. Takes precedence over count/seed. */
  bars?: WaveformBar[];
  /** Fixed bar width in px. When omitted bars flex to fill the container. */
  barWidth?: number;
  /** Gap between bars in px. */
  gap?: number;
  sx?: SxProps<Theme>;
}

/**
 * The design's signature element: a bar-graph waveform. Appears at nine
 * different sizes across the ten screens, always driven by the same generator,
 * which is why a given project's waveform is recognisable wherever it shows up.
 */
export const Waveform: React.FC<WaveformProps> = ({
  count = 40,
  seed = 0,
  height,
  played,
  bars,
  barWidth,
  gap = 2,
  sx,
}) => {
  const data = useMemo(
    () => bars ?? generateBars(count, seed, height, played),
    [bars, count, seed, height, played]
  );

  return (
    <Box
      aria-hidden
      sx={{
        height,
        display: 'flex',
        alignItems: 'center',
        gap: `${gap}px`,
        minWidth: 0,
        ...sx,
      }}
    >
      {data.map((bar, i) => (
        <Box
          key={i}
          sx={{
            ...(barWidth
              ? { width: `${barWidth}px`, flex: 'none' }
              : { flex: '1 1 0', minWidth: '2px' }),
            height: `${bar.h}px`,
            borderRadius: '999px',
            background: bar.c,
          }}
        />
      ))}
    </Box>
  );
};

export default Waveform;
